import React, { useCallback, useRef, useMemo, useEffect } from "react";
import { Sprite, Container } from "@pixi/react";
import anime from "animejs";
import { useSelector } from "react-redux";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { useFreeSpinControl } from "../../../../Contexts/FreeSpinControlContext";
import { PixiSettings } from "../../../../pixiSettings";
import { getUITexture } from "../../../../assets";
import { selectFreeSpinActive, selectFreeSpinComplete } from "../../../../State/selectors";
import { selectCounterAnimationFinished, selectFreeSpinCycleComplete, selectFreeSpinTransitionActive } from "../../../../State/Slices/animationsSlice";
import store from "../../../../State/Store/store";
import { soundManager } from "../../../../SoundManager";
import { SOUND_NAME } from "../../../../sounds";
import { createButtonPressAnimation } from "../../../../utilities";

const PLAY_BUTTON_CONFIG = {
  BASE_WIDTH: 170,
  BASE_HEIGHT: 170,
  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE_SMALL]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.65,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.2,
      },
    },
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.65,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.35,
      },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.75,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.4,
      },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      deviceMultiplier: 0.9,
    },
  },
};

const PLAY_BUTTON_POSITION_CONFIG = {
  [PixiSettings.DEVICE.MOBILE_SMALL]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      x: (toolbar) => toolbar.width / 2,
      y: (toolbar, buttonConfig, dimensions) => -dimensions.height / 10.5,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      x: (toolbar, buttonConfig) => toolbar.width - buttonConfig.width / 1.65,
      y: (toolbar, buttonConfig, dimensions) =>
        -dimensions.height / 2 + buttonConfig.height / 2.0,
    },
  },
  [PixiSettings.DEVICE.MOBILE]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      x: (toolbar) => toolbar.width / 2,
      y: (toolbar, buttonConfig, dimensions) => -dimensions.height / 10.5,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      x: (toolbar, buttonConfig) => toolbar.width - buttonConfig.width / 1.65,
      y: (toolbar, buttonConfig, dimensions) =>
        -dimensions.height / 2 + buttonConfig.height / 2.0,
    },
  },
  [PixiSettings.DEVICE.TABLET]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      x: (toolbar, buttonConfig) => toolbar.width - buttonConfig.width / 1.6,
      y: (toolbar, buttonConfig, dimensions) =>
        toolbar.height - buttonConfig.height * 2.2,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      x: (toolbar, buttonConfig) => toolbar.width - buttonConfig.width / 1.6,
      y: (toolbar, buttonConfig) => toolbar.height - buttonConfig.height * 3.2,
    },
  },
  [PixiSettings.DEVICE.DESKTOP]: {
    x: (toolbar, buttonConfig) => toolbar.width - buttonConfig.width * 1.275,
    y: (toolbar, buttonConfig) => buttonConfig.width * 0.1,
  },
};

const ButtonFreeSpinPlay = ({ toolbar }) => {
  const buttonRef = useRef(null);
  const hoverContainerRef = useRef(null);
  const hoverAnimRef = useRef(null);
  const autoplayStartedRef = useRef(false);

  const { dimensions, uiSymbolSize, device, orientation, getResponsiveConfig, isMobileSmall } =
    useDimensions();

  const freeSpinActive = useSelector(selectFreeSpinActive);
  const freeSpinTransitionActive = useSelector(selectFreeSpinTransitionActive);
  const freeSpinComplete = useSelector(selectFreeSpinComplete);
  const counterAnimationFinished = useSelector(selectCounterAnimationFinished);
  const freeSpinCycleComplete = useSelector(selectFreeSpinCycleComplete);
  const { startFreeSpinAutoplay, skipFreeSpinWinCycle } = useFreeSpinControl();

  const buttonConfig = useMemo(
    () => getResponsiveConfig(PLAY_BUTTON_CONFIG, true),
    [getResponsiveConfig]
  );

  const buttonPosition = useMemo(() => {
    const realDevice = isMobileSmall
      ? PixiSettings.DEVICE.MOBILE_SMALL
      : device;
    const deviceConfig = PLAY_BUTTON_POSITION_CONFIG[realDevice];
    const posConfig = deviceConfig?.[orientation] ?? deviceConfig;
    return {
      x: posConfig.x(toolbar, buttonConfig, dimensions, uiSymbolSize),
      y: posConfig.y(toolbar, buttonConfig, dimensions, uiSymbolSize),
    };
  }, [device, orientation, toolbar, buttonConfig, dimensions, uiSymbolSize, isMobileSmall]);

  // Can skip when: wins are showing, counter done, but lines still cycling
  const canSkip = freeSpinComplete && counterAnimationFinished && !freeSpinCycleComplete;

  const iconTexture = getUITexture("icon_play");

  // Auto-start free spin loop when freeSpinActive is true AND transition is fully done
  useEffect(() => {
    if (freeSpinActive && !freeSpinTransitionActive && !autoplayStartedRef.current) {
      autoplayStartedRef.current = true;
      startFreeSpinAutoplay();
    }
    if (!freeSpinActive) {
      autoplayStartedRef.current = false;
    }
  }, [freeSpinActive, freeSpinTransitionActive, startFreeSpinAutoplay]);

  const handlePointerOver = useCallback(() => {
    if (!hoverContainerRef.current || !canSkip) return;
    if (buttonRef.current) {
      const hoverTex = getUITexture("icon_play_hover");
      if (hoverTex) buttonRef.current.texture = hoverTex;
    }
    if (hoverAnimRef.current) hoverAnimRef.current.pause();
    hoverAnimRef.current = anime({
      targets: hoverContainerRef.current.scale,
      x: 1.05, y: 1.05,
      duration: 150, easing: "easeOutQuad",
    });
  }, [canSkip]);

  const handlePointerOut = useCallback(() => {
    if (!hoverContainerRef.current) return;
    if (buttonRef.current) {
      const normalTex = getUITexture("icon_play");
      if (normalTex) buttonRef.current.texture = normalTex;
    }
    if (hoverAnimRef.current) hoverAnimRef.current.pause();
    hoverAnimRef.current = anime({
      targets: hoverContainerRef.current.scale,
      x: 1.0, y: 1.0,
      duration: 150, easing: "easeOutQuad",
    });
  }, []);

  // Button action: skip win line cycling only
  const handlePointerDown = useCallback(() => {
    if (canSkip) {
      soundManager.play(SOUND_NAME.button_click);
      if (buttonRef.current) {
        createButtonPressAnimation(buttonRef.current, {
          targetWidth: buttonConfig.width,
          targetHeight: buttonConfig.height,
        });
      }
      skipFreeSpinWinCycle();
    }
  }, [canSkip, skipFreeSpinWinCycle, buttonConfig.width, buttonConfig.height]);

  // Spacebar support — skip only
  useEffect(() => {
    if (!freeSpinActive) return;

    const handleKeyDown = (event) => {
      if (event.code !== "Space" && event.key !== " " && event.keyCode !== 32) return;
      if (event.repeat) return;

      event.preventDefault();

      // Read latest state for skip check
      const state = store.getState();
      const spinning = state.featureGame;
      const anims = state.animations;

      // Skip mode: counter done, lines still cycling
      const skipAvailable = spinning.freeSpinComplete &&
        anims.counterAnimationFinished &&
        !anims.freeSpinCycleComplete;

      if (skipAvailable) {
        soundManager.play(SOUND_NAME.button_click);
        if (buttonRef.current) {
          createButtonPressAnimation(buttonRef.current, {
            targetWidth: buttonConfig.width,
            targetHeight: buttonConfig.height,
          });
        }
        skipFreeSpinWinCycle();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [freeSpinActive, skipFreeSpinWinCycle, buttonConfig.width, buttonConfig.height]);

  if (!freeSpinActive) return null;

  return (
    <Container
      name="button_free_spin_play"
      x={buttonPosition.x}
      y={buttonPosition.y}
    >
      <Container ref={hoverContainerRef}>
        <Sprite
          ref={buttonRef}
          texture={iconTexture}
          name="button_free_spin_play_icon"
          anchor={0.5}
          width={buttonConfig.width}
          height={buttonConfig.height}
          x={0}
          y={0}
          eventMode={!freeSpinTransitionActive && canSkip ? "static" : "none"}
          cursor={!freeSpinTransitionActive && canSkip ? "pointer" : "default"}
          onpointerover={handlePointerOver}
          onpointerout={handlePointerOut}
          onpointerdown={handlePointerDown}
          alpha={!freeSpinTransitionActive && canSkip ? 1 : 0.5}
          resolution={2}
        />
      </Container>
    </Container>
  );
};

export default ButtonFreeSpinPlay;
